import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { deviceService } from "../services/deviceService";

// Query keys for consistent cache management
export const deviceKeys = {
  all: ["devices"],
  lists: () => [...deviceKeys.all, "list"],
  list: (filters) => [...deviceKeys.lists(), { filters }],
  details: () => [...deviceKeys.all, "detail"],
  detail: (id) => [...deviceKeys.details(), id],
  history: (id) => [...deviceKeys.all, "history", id],
  brands: ["device-brands"],
  brandDevices: (brandId) => [...deviceKeys.all, "brand", brandId],
};

// Custom hooks for devices
export const useDevices = (options = {}) => {
  return useQuery({
    queryKey: deviceKeys.lists(),
    queryFn: deviceService.getDevices,
    ...options,
  });
};

export const useDevice = (id, options = {}) => {
  return useQuery({
    queryKey: deviceKeys.detail(id),
    queryFn: () => deviceService.getDevice(id),
    enabled: !!id,
    ...options,
  });
};

export const useDeviceHistory = (id, options = {}) => {
  return useQuery({
    queryKey: deviceKeys.history(id),
    queryFn: () => deviceService.getDeviceHistory(id),
    enabled: !!id,
    ...options,
  });
};

export const useDeviceBrands = (options = {}) => {
  return useQuery({
    queryKey: deviceKeys.brands,
    queryFn: deviceService.getDeviceBrands,
    ...options,
  });
};

export const useDevicesByBrand = (brandId, options = {}) => {
  return useQuery({
    queryKey: deviceKeys.brandDevices(brandId),
    queryFn: () => deviceService.getDevicesByBrand(brandId),
    enabled: !!brandId,
    ...options,
  });
};

// Mutation hooks for devices
export const useCreateDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deviceService.createDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
};

export const useUpdateDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => deviceService.updateDevice(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: deviceKeys.detail(variables.id),
      });
    },
  });
};

export const useUpdateDeviceName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }) =>
      deviceService.updateDeviceName(id, { name }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: deviceKeys.detail(variables.id),
      });
    },
  });
};

export const useDeleteDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deviceService.deleteDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
};

export const useDeleteFramesBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deviceId, frames }) =>
      deviceService.deleteFramesBatch(deviceId, frames),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: deviceKeys.history(variables.deviceId),
      });
    },
  });
};

export const useDeleteAllFrames = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deviceId) => deviceService.deleteAllFrames(deviceId),
    onSuccess: (data, deviceId) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.history(deviceId) });
    },
  });
};

export const useUpdateDeviceOwnership = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deviceId, organisationId }) =>
      deviceService.changeDeviceOwnership(deviceId, {
        organisation_id: organisationId,
      }),
    onSuccess: (data, variables) => {
      // Invalidate device-related queries
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });

      // Make sure we're using the correct device ID for the detail query
      // Convert to number to match the query key format
      const deviceId = parseInt(variables.deviceId);
      queryClient.invalidateQueries({
        queryKey: deviceKeys.detail(deviceId),
      });

      // Also invalidate with broader pattern to catch any variations
      queryClient.invalidateQueries({
        queryKey: ["devices", "detail"],
        exact: false,
      });

      // Invalidate organisation-related queries since device ownership changed
      queryClient.invalidateQueries({
        queryKey: ["organisations"],
        exact: false,
      });
    },
  });
};
